import React from 'react';
import { BarChart2, Kanban, List as ListIcon, Plus, Search, Settings, X } from 'lucide-react';
import { AppSelect } from '../../../components/AppSelect';
import { SETTINGS_SELECT_TRIGGER_CLASS } from '../../settings/utils/settingsSectionStyles';
import type { LeadPipeline } from '../../../types';
import type { ProjectListView } from '../../../utils/projectRoutes';
import {
  ALL_FILTER_VALUE,
  LeadFilterPopover,
  NO_CATEGORY_FILTER_VALUE,
  type LeadFilterState,
} from './LeadFilterPopover';
import { LEAD_DATE_RANGE_OPTIONS } from '../utils/leadListView';

const VIEW_OPTIONS: Array<{ view: ProjectListView; label: string; Icon: typeof Kanban }> = [
  { view: 'pipeline', label: 'Pipeline', Icon: Kanban },
  { view: 'list', label: 'Liste', Icon: ListIcon },
  { view: 'dashboard', label: 'Auswertung', Icon: BarChart2 },
];

type LeadListToolbarProps = {
  viewMode: ProjectListView;
  onViewModeChange: (view: ProjectListView) => void;
  resultCount: number;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  pipelines: LeadPipeline[];
  selectedPipelineId: string | null;
  onPipelineChange: (pipelineId: string) => void;
  onOpenPipelineSettings: () => void;
  categories: Array<{ key: string; label: string }>;
  statuses: string[];
  hasNoCategory: boolean;
  filters: LeadFilterState;
  onFiltersChange: (next: Partial<LeadFilterState>) => void;
  onResetFilters: () => void;
  onCreateLead: () => void;
};

export function LeadListToolbar({
  viewMode,
  onViewModeChange,
  resultCount,
  searchTerm,
  onSearchTermChange,
  pipelines,
  selectedPipelineId,
  onPipelineChange,
  onOpenPipelineSettings,
  categories,
  statuses,
  hasNoCategory,
  filters,
  onFiltersChange,
  onResetFilters,
  onCreateLead,
}: LeadListToolbarProps) {
  const isList = viewMode === 'list';
  const isPipeline = viewMode === 'pipeline';
  const showCount = isList || isPipeline;
  const showFilterPopover = isList || isPipeline;

  const categoryLabel =
    filters.category === NO_CATEGORY_FILTER_VALUE
      ? 'Keine Kategorie'
      : categories.find((c) => c.key === filters.category)?.label ?? filters.category;
  const dateRangeLabel =
    LEAD_DATE_RANGE_OPTIONS.find((o) => o.value === filters.dateRange)?.label ?? '';

  const filterChips: Array<{ id: string; label: string; onRemove: () => void }> = [];
  if (showFilterPopover) {
    if (filters.category !== ALL_FILTER_VALUE) {
      filterChips.push({
        id: 'category',
        label: `Kategorie: ${categoryLabel}`,
        onRemove: () => onFiltersChange({ category: ALL_FILTER_VALUE }),
      });
    }
    if (filters.status !== ALL_FILTER_VALUE) {
      filterChips.push({
        id: 'status',
        label: `Status: ${filters.status}`,
        onRemove: () => onFiltersChange({ status: ALL_FILTER_VALUE }),
      });
    }
    if (filters.dateRange !== 'all') {
      filterChips.push({
        id: 'dateRange',
        label: dateRangeLabel,
        onRemove: () => onFiltersChange({ dateRange: 'all' }),
      });
    }
  }

  // Suche hat ihr eigenes Clear-Kreuz — „Filter zurücksetzen“ nur bei echten Filtern.
  const hasActiveFilters = filterChips.length > 0;

  // Gefülltes Feld statt Rahmen: passt neben die rahmenlosen Bedienelemente,
  // bleibt aber als Eingabe erkennbar. Der Rahmen kommt erst beim Fokus.
  const searchField = (
    <div className="relative min-w-0 flex-1 md:w-56 md:flex-none lg:w-64">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-text-subtle" />
      <input
        type="search"
        value={searchTerm}
        onChange={(e) => onSearchTermChange(e.target.value)}
        placeholder="Projekte durchsuchen…"
        aria-label="Projekte durchsuchen"
        className="h-9 w-full rounded-md border border-transparent bg-app-surface-muted pl-9 pr-8 text-sm text-app-text outline-none placeholder:text-app-text-subtle hover:bg-app-hover focus:border-pipedrive-blue focus:bg-app-surface focus:ring-2 focus:ring-pipedrive-blue/20 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
      />
      {searchTerm ? (
        <button
          type="button"
          onClick={() => onSearchTermChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-app-text-subtle hover:text-app-text-muted"
          aria-label="Suche löschen"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="shrink-0 border-b border-app-border bg-app-surface">
      {/* Desktop */}
      <div className="hidden h-14 items-center justify-between gap-4 px-4 md:flex">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex shrink-0 items-center gap-1 rounded-md bg-app-surface-muted p-0.5">
            {VIEW_OPTIONS.map(({ view, label, Icon }) => (
              <button
                key={view}
                type="button"
                title={label}
                aria-label={label}
                aria-pressed={viewMode === view}
                onClick={() => onViewModeChange(view)}
                className={`rounded p-1.5 transition-all ${
                  viewMode === view
                    ? 'bg-app-surface text-pipedrive-blue shadow-sm'
                    : 'text-app-text-subtle hover:text-app-text'
                }`}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
          {showCount ? (
            <span className="truncate text-sm tabular-nums text-app-text-subtle" aria-live="polite">
              {`${resultCount} Projekte`}
            </span>
          ) : null}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {(isList || isPipeline) && pipelines.length > 1 ? (
            <AppSelect
              value={selectedPipelineId ?? ''}
              onChange={onPipelineChange}
              options={pipelines.map((p) => ({ value: p.id, label: p.name }))}
              className="w-44"
              variant="ghost"
              triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
              icon={<Kanban className="h-3.5 w-3.5" aria-hidden />}
              aria-label="Pipeline wählen"
            />
          ) : null}

          {showFilterPopover ? (
            <LeadFilterPopover
              categories={categories}
              statuses={statuses}
              hasNoCategory={hasNoCategory}
              value={filters}
              onChange={onFiltersChange}
              onReset={onResetFilters}
              activeCount={filterChips.length}
            />
          ) : null}

          <button
            type="button"
            title="Pipeline-Phasen bearbeiten"
            aria-label="Pipeline-Phasen bearbeiten"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-app-text-subtle transition-colors hover:bg-app-hover hover:text-pipedrive-blue"
            onClick={onOpenPipelineSettings}
          >
            <Settings className="h-4 w-4" />
          </button>

          {hasActiveFilters ? (
            <button
              type="button"
              className="flex items-center gap-1 rounded-full bg-app-surface-muted px-2 py-1 text-xs font-medium text-app-text-muted transition-colors hover:bg-app-hover"
              onClick={onResetFilters}
            >
              <X className="h-3 w-3" />
              Filter zurücksetzen
            </button>
          ) : null}

          {isList || isPipeline ? searchField : null}

          <button
            type="button"
            className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838]"
            onClick={onCreateLead}
          >
            <Plus className="h-4 w-4" />
            Projekt
          </button>
        </div>
      </div>

      {/* Mobile — immer Listenansicht, deshalb Suche + Filter-Popover. */}
      <div className="flex items-center gap-2 px-3 py-2 md:hidden">
        <span className="shrink-0 text-sm tabular-nums text-app-text-subtle" aria-live="polite">
          {resultCount}
        </span>
        {searchField}
        <LeadFilterPopover
          categories={categories}
          statuses={statuses}
          hasNoCategory={hasNoCategory}
          value={filters}
          onChange={onFiltersChange}
          onReset={onResetFilters}
          activeCount={
            (filters.category !== ALL_FILTER_VALUE ? 1 : 0) +
            (filters.status !== ALL_FILTER_VALUE ? 1 : 0) +
            (filters.dateRange !== 'all' ? 1 : 0)
          }
        />
      </div>

      {/* Aktive Filter als Chips — Pipeline und Liste (Desktop). */}
      {showFilterPopover && filterChips.length > 0 ? (
        <div className="hidden flex-wrap items-center gap-2 border-t border-app-border px-3 py-2 md:flex md:px-4">
          {filterChips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={chip.onRemove}
              className="inline-flex items-center gap-1.5 rounded-full bg-app-surface-muted py-1 pl-2.5 pr-2 text-xs font-medium text-app-text-muted transition-colors hover:bg-app-hover"
            >
              {chip.label}
              <X className="h-3 w-3 text-app-text-subtle" aria-hidden />
              <span className="sr-only">Filter entfernen</span>
            </button>
          ))}
          <button
            type="button"
            onClick={onResetFilters}
            className="text-xs font-medium text-app-text-subtle underline-offset-2 transition-colors hover:text-app-text hover:underline"
          >
            Alle zurücksetzen
          </button>
        </div>
      ) : null}
    </div>
  );
}
